import { Calendar, CalendarTabs } from '@sketchnote/shared-components';
import { memo, useMemo, useRef, useState } from 'react';
import type { OnChangeDateRangeCallback } from 'react-calendar';
import useOnClickOutside from 'use-onclickoutside';

interface DateRangePickerProps {
  value: [Date] | [Date, Date] | null;
  onChange: OnChangeDateRangeCallback;
  onDoneClick: () => void;
  inputClassName?: string;
  placeholderText?: string;
  dateFormat?: string;
  initialTab?: CalendarTabs;
}

import { GridApi } from 'ag-grid-community';
import { formatISO, parseISO } from 'date-fns';
import { useTableStore } from 'src/features/list/store';

const DateCell = ({
  data: item,
  api,
  onChange,
}: {
  data: any;
  api: GridApi;
  onChange?: (item, value) => void;
}) => {
  const [updatedDates, setUpdatedDates] = useState<string[] | undefined>();
  const myRef = useRef(null);

  const { setItem, onUpdateItemField } = useTableStore((s) => ({
    setItem: s.setItem,
    onUpdateItemField: s.onUpdateItemField,
  }));

  const handleChange = (values) => {
    const dateValues = values.length ? values?.map((date) => formatISO(date)) : [];
    setUpdatedDates(dateValues);
  };

  const assignedDates = useMemo(() => {
    if (item?.assignDate?.length)
      return item.assignDate
        .map((date) => (date ? parseISO(date) : undefined))
        .filter((date) => !!date);
    return [];
  }, [item]);

  const handleDoneClick = () => {
    if (updatedDates !== undefined) {
      onUpdateItemField({
        item: {
          assignDate: updatedDates,
          id: item.id!,
        },
        keys: ['assignDate'],
      });
      setItem({
        columnId: item.isSubtask ? item.parentColumn : item.colId,
        itemId: item.id,
        item: { ...item, assignDate: updatedDates },
      });
      setUpdatedDates([]);
    }
    onChange?.(item, updatedDates);
    api.stopEditing();
  };

  useOnClickOutside(myRef, () => {
    api.stopEditing();
  });

  return (
    <div ref={myRef} className=" text-blue-600">
      <DateRangePicker
        value={assignedDates.length > 0 ? (assignedDates as [Date] | [Date, Date]) : null}
        onChange={handleChange}
        onDoneClick={handleDoneClick}
        inputClassName="date-picker flex items-center bg-white w-full rounded-sm h-36 text-left"
        initialTab={assignedDates.length === 1 ? 'startEndDate' : 'date'}
      />
    </div>
  );
};

const DateRangePicker = ({
  value,
  onChange,
  onDoneClick,
  initialTab = 'date',
}: DateRangePickerProps) => {
  const { isReadOnly } = useTableStore();

  const handleDoneClick = () => {
    onDoneClick();
  };

  if (isReadOnly) return <span />;

  return (
    <Calendar
      value={value}
      onChange={onChange}
      endDateFirst
      onDone={handleDoneClick}
      initialTab={initialTab}
    />
  );
};

export const DateCellEditor = memo(DateCell);
